import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Toolbar,
  AppBar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';

import { getTemplate, CMSTemplate } from '../../../../firebase/services/cmsService';

const TemplatePreview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<CMSTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadTemplate(id);
    }
  }, [id]);

  const loadTemplate = async (templateId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const templateData = await getTemplate(templateId);
      if (templateData) {
        setTemplate(templateData);
      } else {
        setError('Template not found');
      }
    } catch (err: any) {
      console.error('Error loading template:', err);
      setError(err.message || 'Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const renderSection = (section: any) => {
    if (!section.isVisible) return null;

    const sectionContent = (
      <Box key={section.id} sx={{ mb: 4 }}>
        {section.title && (
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            sx={{ fontWeight: 'bold', mb: 3 }}
          >
            {section.title}
          </Typography>
        )}
        
        {section.content && (
          <Typography 
            component="div"
            sx={{ 
              '& p': { mb: 2 },
              '& h1, & h2, & h3, & h4, & h5, & h6': { 
                fontWeight: 'bold',
                mt: 3,
                mb: 2,
              },
              '& ul, & ol': { 
                pl: 3,
                mb: 2,
              },
            }}
            dangerouslySetInnerHTML={{ __html: section.content }}
          />
        )}
      </Box>
    );

    // Render different section types with appropriate layouts
    switch (section.type) {
      case 'hero':
        return (
          <Box
            key={section.id}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              py: 8,
              mb: 4,
              textAlign: 'center',
            }}
          >
            <Container maxWidth="lg">
              {sectionContent}
            </Container>
          </Box>
        );

      case 'image_text':
        return (
          <Container key={section.id} maxWidth="lg" sx={{ mb: 4 }}>
            <Card>
              <CardContent sx={{ p: 4 }}>
                {sectionContent}
              </CardContent>
            </Card>
          </Container>
        );

      case 'services':
        return (
          <Box key={section.id} sx={{ bgcolor: 'grey.50', py: 6, mb: 4 }}>
            <Container maxWidth="lg">
              {sectionContent}
            </Container>
          </Box>
        );

      case 'contact_info':
        return (
          <Container key={section.id} maxWidth="lg" sx={{ mb: 4 }}>
            <Paper sx={{ p: 4 }}>
              {sectionContent}
            </Paper>
          </Container>
        );

      default:
        return (
          <Container key={section.id} maxWidth="lg">
            {sectionContent}
          </Container>
        );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/business/online/templates')}
          sx={{ mt: 2 }}
        >
          Back to Templates
        </Button>
      </Box>
    );
  }

  if (!template) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Template not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/business/online/templates')}
          sx={{ mt: 2 }}
        >
          Back to Templates
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Preview Toolbar */}
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/admin/business/online/templates')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <PreviewIcon sx={{ mr: 1 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Template Preview: {template.name}
          </Typography>
          <Button
            color="inherit"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/business/online/templates/${template.id}/edit`)}
          >
            Edit Template
          </Button>
        </Toolbar>
      </AppBar>

      {/* Template Preview Content */}
      <Box
        sx={{
          backgroundColor: template.layoutSettings?.backgroundColor || '#ffffff',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {/* Page Header */}
        <Box sx={{ py: 4, mb: 4, bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Container maxWidth={template.layoutSettings?.containerMaxWidth || 'lg'}>
            <Typography 
              variant="h1" 
              component="h1" 
              sx={{ 
                fontWeight: 'bold',
                textAlign: 'center',
                mb: 2
              }}
            >
              Sample Page Title
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ textAlign: 'center' }}
            >
              This is how your template will look when applied to a page
            </Typography>
          </Container>
        </Box>

        {/* Template Sections */}
        <Box sx={{ pb: 6 }}>
          {template.sections
            ?.sort((a, b) => a.order - b.order)
            .filter(section => section.isVisible)
            .map(section => renderSection(section))
          }
        </Box>

        {/* Footer spacer */}
        <Box sx={{ py: 4 }} />
      </Box>
    </Box>
  );
};

export default TemplatePreview; 